// How big are they? The boards (which worlds each one draws, and at what scale), the words under each picture, the
// credits, and the way in: openSizes() opens the page on the board that holds a body, with that body picked out.
// Kept apart from the screen (SizeCompare.tsx) so all of it can be tested without a page (tests/sizes.test.ts).
// The sizes and pictures: src/data/sizes.ts, made by scripts/build-sizes.py.
import { signal } from '@preact/signals';
import { TAIL_SEVERAL } from '../data/islam';
import { EARTH_KM, SIZE_BODIES, type SizeBody, type SizeLicence } from '../data/sizes';
import { panel } from '../state';

export const BODY = new Map<string, SizeBody>(SIZE_BODIES.map((b) => [b.id, b]));

/** Worlds drawn at one scale: `spanKm` is what the board's full width stands for. In `bodies`, BREAK starts a new
 * line (so Ganymede and Titan stand beside Mercury rather than beside Earth). */
export interface SizeGroup {
  title?: string;
  bodies: string[];
  spanKm: number;
  bar: { km: number; label: string };
}

export interface SizeBoard {
  id: string;
  /** Short name for the row of buttons at the top of the page. */
  short: string;
  title: string;
  lead: string;
  groups: SizeGroup[];
  /** A body drawn as a great arc along the top, at the first group's scale: only its edge fits (the Sun). */
  arc?: string;
  /** Extra words under each name: what kind of world it is (the dwarf planets' board). */
  tags?: boolean;
}

const PLANETS = ['mercury', 'venus', 'earth', 'mars', 'jupiter', 'saturn', 'uranus', 'neptune'];
/** In a group's bodies: start a new line here. */
export const BREAK = '/';

export const BOARDS: SizeBoard[] = [
  {
    id: 'earth-moon',
    short: 'Earth and Moon',
    title: 'Earth and the Moon',
    lead: 'Earth is more than three and a half times as wide as the Moon.',
    groups: [{ bodies: ['earth', 'moon'], spanKm: 21000, bar: { km: 2000, label: '2,000 km' } }],
  },
  {
    id: 'planets',
    short: 'Planets',
    title: 'The planets',
    lead: 'All eight, in order from the Sun. Jupiter is 11 times as wide as Earth.',
    groups: [{ bodies: PLANETS, spanKm: 300000, bar: { km: 50000, label: '50,000 km' } }],
  },
  {
    id: 'sun',
    short: 'The Sun',
    title: 'The Sun and the planets',
    lead: 'The Sun is 109 times as wide as Earth: only the edge of it fits here.',
    arc: 'sun',
    groups: [{ bodies: PLANETS, spanKm: 1391400 / 2.6, bar: { km: 100000, label: '100,000 km' } }],
  },
  {
    id: 'moons',
    short: 'Moons',
    title: 'The largest moons',
    lead: 'Ganymede, a moon of Jupiter, and Titan, a moon of Saturn, are both wider than the planet Mercury.',
    groups: [{
      bodies: ['earth', BREAK, 'ganymede', 'titan', 'mercury', 'callisto', 'io', 'moon', 'europa', 'triton', 'titania', 'rhea', 'oberon', 'iapetus', 'umbriel', 'ariel', 'dione', 'tethys', 'enceladus', 'miranda', 'mimas', 'hyperion'],
      spanKm: 32000,
      bar: { km: 2000, label: '2,000 km' },
    }],
  },
  {
    id: 'dwarfs',
    short: 'Dwarf planets',
    title: 'Dwarf planets and the largest asteroids',
    lead: 'Ceres, Pluto, Eris, Haumea and Makemake are the five dwarf planets; Gonggong, Quaoar and Sedna are candidates. With the Moon and Earth for scale.',
    tags: true,
    groups: [{
      bodies: ['earth', 'moon', BREAK, 'pluto', 'charon', 'eris', 'haumea', 'makemake', 'ceres', 'gonggong', 'quaoar', 'sedna', 'vesta'],
      spanKm: 28000,
      bar: { km: 2000, label: '2,000 km' },
    }],
  },
  {
    id: 'small',
    short: 'Small worlds',
    title: 'Small worlds',
    lead: 'Too small for their own gravity to pull them into balls, so each is compared with Earth by its average width. The second row is drawn ten times larger; Comet 67P is in both.',
    groups: [
      { title: 'Moons of Mars, a comet and a world beyond Neptune', bodies: ['arrokoth', 'phobos', 'deimos', '67p'], spanKm: 60, bar: { km: 10, label: '10 km' } },
      { title: 'Ten times larger: the comet and three small asteroids', bodies: ['67p', 'ryugu', 'bennu', 'dimorphos'], spanKm: 6, bar: { km: 1, label: '1 km (1,000 m)' } },
    ],
  },
  {
    id: 'red-spot',
    short: 'Red Spot',
    title: 'Jupiter’s Great Red Spot',
    lead: 'A storm wider than our whole planet, seen by Hubble in June 2019. It is shrinking: when Voyager passed in 1979 it was about 1.8 times as wide as Earth.',
    groups: [{ bodies: ['redspot', 'earth'], spanKm: 62000, bar: { km: 5000, label: '5,000 km' } }],
  },
];

/** A group's worlds, without its line breaks. */
export const groupBodies = (g: SizeGroup) => g.bodies.filter((id) => id !== BREAK);
/** Every world a board draws, once each, in order (its arc first). */
export const boardBodies = (b: SizeBoard) => [...new Set([...(b.arc ? [b.arc] : []), ...b.groups.flatMap(groupBodies)])];

/** Every board a body appears on, in page order. */
export function boardsOf(id: string): SizeBoard[] {
  return BOARDS.filter((b) => boardBodies(b).includes(id));
}

/** The board a body belongs to most: where openSizes() takes it. */
export function homeBoard(id: string): SizeBoard | undefined {
  const b = BODY.get(id);
  if (!b) return undefined;
  const want = id === 'earth' || id === 'moon' ? 'earth-moon'
    : b.kind === 'storm' ? 'red-spot'
    : b.kind === 'star' ? 'sun'
    : b.kind === 'planet' ? 'planets'
    : b.kind === 'dwarf' || b.kind === 'candidate' || id === 'charon' || id === 'vesta' ? 'dwarfs'
    : b.kind === 'moon' && b.km > 200 ? 'moons'
    : 'small';
  return BOARDS.find((x) => x.id === want);
}

/**
 * A body named by this page's ids ('titan'), or as the Gallery and the research name it ('moon-titan', 'Titan',
 * 'moon-phobos'). Unknown names give null.
 */
export function sizeId(name: string | null | undefined): string | null {
  if (!name) return null;
  const n = String(name).trim().toLowerCase().replace(/^the /, '');
  const id = n.startsWith('moon-') ? n.slice(5) : n;
  return BODY.has(id) ? id : null;
}

/** The page open, the board to show and the body picked out on it. `at` tells one request from the next. */
export const sizesFocus = signal<{ board: string | null; body: string | null; at: number }>({ board: null, body: null, at: 0 });

/** Open "How big are they?", at the board that holds `bodyId` with it picked out, or at the top. */
export function openSizes(bodyId?: string) {
  const id = sizeId(bodyId);
  sizesFocus.value = { board: id ? homeBoard(id)?.id ?? null : null, body: id, at: Date.now() };
  panel.value = 'sizes';
}

export function closeSizes() {
  if (panel.peek() === 'sizes') panel.value = null;
}

// The Gallery's page for each body: moons and dwarf planets have pages of their own ('moon-io', 'dwarf-eris'), the
// smaller worlds share one; a body not listed here opens the Gallery's page of the same name ('jupiter', 'moon').
const GALLERY_PAGE: Record<string, string> = {
  earth: 'earth-from-afar',
  redspot: 'jupiter',
  ganymede: 'moon-ganymede', titan: 'moon-titan', callisto: 'moon-callisto', io: 'moon-io', europa: 'moon-europa',
  triton: 'moon-triton', rhea: 'moon-rhea', iapetus: 'moon-iapetus', dione: 'moon-dione', tethys: 'moon-tethys',
  enceladus: 'moon-enceladus', mimas: 'moon-mimas', hyperion: 'moon-hyperion', miranda: 'moon-miranda', charon: 'moon-charon',
  titania: 'moon-uranus-moons', oberon: 'moon-uranus-moons', umbriel: 'moon-uranus-moons', ariel: 'moon-uranus-moons',
  phobos: 'moon-phobos-deimos', deimos: 'moon-phobos-deimos',
  ceres: 'dwarf-ceres', eris: 'dwarf-eris', haumea: 'dwarf-haumea', makemake: 'dwarf-makemake',
  gonggong: 'dwarf-candidates', quaoar: 'dwarf-candidates', sedna: 'dwarf-candidates',
  vesta: 'asteroids', bennu: 'asteroids', ryugu: 'asteroids', dimorphos: 'asteroids',
  '67p': 'comets',
  arrokoth: 'kuiper',
};
export const galleryPageFor = (id: string) => GALLERY_PAGE[id] ?? id;

// ---------------------------------------------------------------- the words under each picture
const num = (n: number, digits = 0) => n.toLocaleString('en-GB', { maximumFractionDigits: digits, minimumFractionDigits: digits });

/** A length as people say it: metres below a kilometre, one decimal below 100 km, whole kilometres above. */
export function fmtKm(km: number): string {
  if (km < 1) return `${num(Math.round(km * 1000))}\u00a0m`;
  if (km < 100) return `${num(km, km < 10 ? 1 : km % 1 === 0 ? 0 : 1)}\u00a0km`;
  return `${num(Math.round(km))}\u00a0km`;
}

/** Odd-shaped worlds show their measurements; round ones (and the slightly flattened) their mean diameter. */
const SHOW_DIMS = new Set(['phobos', 'deimos', 'arrokoth', '67p', 'dimorphos', 'haumea', 'ryugu']);

/** The size line under a picture. */
export function sizeText(b: SizeBody): string {
  if (b.kind === 'storm') return `about ${fmtKm(b.km)}\u00a0wide`;
  if (b.dims && SHOW_DIMS.has(b.id)) {
    const metres = b.km < 1;
    const parts = b.dims.map((d) => (metres ? num(Math.round(d * 1000)) : d >= 100 ? num(Math.round(d)) : num(d, d < 10 ? 1 : d % 1 === 0 ? 0 : 1)));
    return `${parts.join(' × ')}\u00a0${metres ? 'm' : 'km'}`;
  }
  return fmtKm(b.km);
}

/** How many times Earth's width: exact enough to compare, plain enough to read. */
export function relEarth(km: number): number {
  return km / EARTH_KM;
}

/** Times Earth's width as written: whole numbers from 100, one decimal from 10, else two significant figures. */
function times(r: number): string {
  return r >= 100 ? num(Math.round(r)) : r >= 10 ? num(r, 1) : r.toPrecision(2);
}

/** For the smallest worlds: how many times wider Earth is, to two significant figures. */
const earthTimes = (r: number) => num(Number((1 / r).toPrecision(2)));

/** "0.27 × Earth", "11.0 × Earth", "109 × Earth"; for the smallest worlds, how many times as wide Earth is. */
export function relText(b: SizeBody): string {
  if (b.id === 'earth') return '1 × Earth';
  const r = relEarth(b.km);
  return r >= 0.01 ? `${times(r)} × Earth` : `Earth is ${earthTimes(r)} times as wide`;
}

/** The same for a screen reader, in words. */
export function spokenRel(b: SizeBody): string {
  if (b.id === 'earth') return 'the yardstick for the others';
  const r = relEarth(b.km);
  return r >= 0.01 ? `${times(r)} times as wide as Earth` : `Earth is ${earthTimes(r)} times as wide`;
}

/** What kind of world, for the dwarf planets' board. */
export function tagOf(b: SizeBody): string | null {
  if (b.id === 'earth' || b.id === 'moon') return null;
  if (b.kind === 'candidate') return 'Candidate';
  if (b.kind === 'dwarf') return 'Dwarf planet';
  if (b.kind === 'asteroid') return 'Asteroid';
  if (b.kind === 'moon' && b.parent) return `Moon of ${BODY.get(b.parent)?.name.replace(/^The /, 'the ') ?? b.parent}`;
  return null;
}

/** The name a moon's planet is known by, for its button's spoken name. */
function parentText(b: SizeBody): string {
  if (b.kind !== 'moon' || !b.parent) return '';
  const p = BODY.get(b.parent);
  return p ? `, a moon of ${p.name.replace(/^The /, '')}` : '';
}

/** Everything a screen reader says for one world on a board. */
export function spokenLabel(b: SizeBody): string {
  const size = b.kind === 'storm' ? sizeText(b) : b.dims && SHOW_DIMS.has(b.id) ? `${sizeText(b).replace(/ × /g, ' by ')}, about ${fmtKm(b.km)} on average` : `${fmtKm(b.km)} across`;
  const kind = b.kind === 'candidate' ? ', a possible dwarf planet' : b.kind === 'dwarf' ? ', a dwarf planet' : parentText(b);
  const blank = b.blank ? `. ${b.blank}` : '';
  return `${b.name}${kind}: ${size.replace(/\u00a0km/g, ' kilometres').replace(/\u00a0m\b/g, ' metres')}; ${spokenRel(b)}${blank}`;
}

// ---------------------------------------------------------------- credits
export interface CreditLine {
  names: string[];
  credit: string;
  licence: SizeLicence;
}

/** A board's picture credits: each credit once, with the worlds it covers, in board order. */
export function creditsFor(board: SizeBoard): CreditLine[] {
  const ids = boardBodies(board);
  const out: CreditLine[] = [];
  for (const id of ids) {
    const p = BODY.get(id)?.photo;
    if (!p) continue;
    const name = BODY.get(id)!.name;
    const line = out.find((l) => l.credit === p.credit && l.licence === p.licence);
    if (line) line.names.push(name);
    else out.push({ names: [name], credit: p.credit, licence: p.licence });
  }
  return out;
}

/** "Earth and the Moon", "Io, Europa and Callisto". */
export function listNames(names: string[]): string {
  const n = names.map((x, i) => (i > 0 ? x.replace(/^The /, 'the ') : x));
  return n.length < 2 ? n.join('') : `${n.slice(0, -1).join(', ')} and ${n[n.length - 1]}`;
}

/** Where colours are not as the eye would see them, in a few words for the board's credits. */
export function colourNotes(board: SizeBoard): string[] {
  const ids = boardBodies(board);
  const pick = (c: string) => ids.filter((id) => BODY.get(id)?.photo?.colour === c).map((id) => BODY.get(id)!.name);
  const notes: string[] = [];
  const enhanced = pick('enhanced');
  const falseColour = pick('false');
  const added = pick('added');
  if (enhanced.length) notes.push(`${listNames(enhanced)}: colours enhanced.`);
  if (falseColour.length) notes.push(`${listNames(falseColour)}: false colour.`);
  if (added.length) notes.push(`${listNames(added)}: colour added to a black-and-white picture.`);
  return notes;
}

export const LICENCE_URL: Partial<Record<SizeLicence, string>> = { 'CC BY 4.0': 'https://creativecommons.org/licenses/by/4.0/' };

/** The page's Islamic reminder: most of the names on it come from false deities and the myths tied to them. The
 * first sentence is a plain statement of fact; the rest is the house wording for a reminder about several names. */
export const NAMES_REMINDER = `Every planet but Earth, the dwarf planets and most of the moons here are named after false deities or figures from the myths tied to them. ${TAIL_SEVERAL}`;
